import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, loadLanguage, type SupportedLanguage } from '../i18n/i18n';

interface LanguageGroup {
  label: string;
  languages: SupportedLanguage[];
}

const LANGUAGE_GROUPS: LanguageGroup[] = [
  {
    label: 'Americas',
    languages: ['en', 'es', 'pt-BR', 'fr'],
  },
  {
    label: 'Europe',
    languages: ['es-ES', 'pt-PT', 'fr', 'ca', 'de', 'it', 'nl', 'pl', 'cs', 'sk', 'hu', 'ro', 'hr', 'sl', 'bg', 'sr', 'el', 'uk', 'ru', 'sv', 'da', 'nb', 'fi', 'et', 'lv', 'lt'],
  },
  {
    label: 'Asia & Pacific',
    languages: ['zh-CN', 'zh-TW', 'ja', 'ko', 'hi', 'bn', 'th', 'vi', 'id', 'ms', 'tl', 'tr'],
  },
];

export function LanguageSwitcher({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentLang = (i18n.language in SUPPORTED_LANGUAGES
    ? i18n.language
    : i18n.language.split('-')[0] in SUPPORTED_LANGUAGES
      ? i18n.language.split('-')[0]
      : 'en') as SupportedLanguage;

  const textColor = variant === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900';
  const menuBg = variant === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const menuText = variant === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100';
  const activeText = variant === 'dark' ? 'text-primary-400' : 'text-primary-600';
  const groupLabel = variant === 'dark' ? 'text-gray-500' : 'text-gray-400';
  const divider = variant === 'dark' ? 'border-gray-700' : 'border-gray-100';

  function LangButton({ code }: { code: SupportedLanguage }) {
    return (
      <button
        onClick={() => { void loadLanguage(code); setOpen(false); }}
        className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-[13px] transition-colors ${
          code === currentLang ? `font-semibold ${activeText}` : menuText
        }`}
      >
        <span className="truncate">{SUPPORTED_LANGUAGES[code]}</span>
        {code === currentLang && (
          <svg className="ml-auto h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
          </svg>
        )}
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${textColor}`}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A8.966 8.966 0 0 1 3 12c0-1.264.26-2.467.73-3.563" />
        </svg>
        <span className="hidden sm:inline">{SUPPORTED_LANGUAGES[currentLang]}</span>
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className={`absolute right-0 top-full z-50 mt-1 rounded-xl border shadow-xl ${menuBg}`}>
          {/* Desktop: 3-column grouped layout */}
          <div className="hidden sm:flex gap-0 p-2 max-h-[70vh] overflow-y-auto">
            {LANGUAGE_GROUPS.map((group, gi) => (
              <div key={group.label} className={`min-w-[160px] px-2 ${gi > 0 ? `border-l ${divider}` : ''}`}>
                <p className={`px-2 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-widest ${groupLabel}`}>
                  {group.label}
                </p>
                {group.languages.map(code => (
                  <LangButton key={code} code={code} />
                ))}
              </div>
            ))}
          </div>

          {/* Mobile: scrollable single-column with group headers */}
          <div className="sm:hidden max-h-[60vh] overflow-y-auto p-2">
            {LANGUAGE_GROUPS.map((group, gi) => (
              <div key={group.label}>
                {gi > 0 && <div className={`my-1.5 border-t ${divider}`} />}
                <p className={`px-2 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-widest ${groupLabel}`}>
                  {group.label}
                </p>
                {group.languages.map(code => (
                  <LangButton key={code} code={code} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
